import { ListActivityRequest_Type, ListActivityRequest_Filter } from '@policyfly/protobuf/patrick'
import uniqBy from 'lodash-es/uniqBy'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { useApiStore } from './api'
import { useAppContextStore } from './appContext'
import { useNotificationStore } from './notification'
import { useSettingsStore } from './settings'
import router from '@/router'
import routeNames from '@/router/routeNames'
import { hotReloadStore } from '@/utils/build'
import { filterEvent, isPolicyView, normalizeEvents, parseEvents } from '@/utils/feed'

import type { MarkAllCommentsReadRequest } from '@policyfly/protobuf/patrick'
import type { FeedItemVisibility } from '@policyfly/protobuf/pfactivity'
import type { FeedEventLog } from 'types/events'
import type { ComputedRef } from 'vue'

export const ACTIVITY_PAGE_SIZE = 25

/**
 * Whether the feed is currently on screen.
 *
 * The feed is only ever loaded while it is being displayed, so anything that
 * invalidates it can refresh unconditionally and be a no-op everywhere else.
 */
function isFeedView (): boolean {
  const { name } = router.currentRoute.value
  return name === routeNames.APPLICATION_FEED || name === routeNames.POLICY_FEED
}

export interface FeedStore {
  /**
   * The events to display, with the active filter applied.
   */
  events: ComputedRef<FeedEventLog[]>
  /**
   * The filter the loaded events were listed with.
   */
  filter: ComputedRef<ListActivityRequest_Filter>
  /**
   * Whether there are further pages of events to load.
   */
  hasMoreEvents: ComputedRef<boolean>
  /**
   * Whether a list request is in flight.
   */
  isLoading: ComputedRef<boolean>
  /**
   * Whether the in-flight request is for a later page rather than the first,
   * so the loaded events stay on screen while it resolves.
   */
  isLoadingMore: ComputedRef<boolean>
  /**
   * Whether there is nothing to display while a request is in flight, so a
   * placeholder can be shown in its place.
   *
   * Every other load leaves the events it already holds on screen until the
   * new ones arrive, rather than blanking the feed on each {@link refresh}.
   */
  isLoadingInitial: ComputedRef<boolean>
  /**
   * The number of unread comments on the application or policy being displayed.
   * Always `0` for protobuf programs, pfactivity cannot determine a count.
   */
  unreadCommentCount: ComputedRef<number>
  /**
   * Loads the first page of events, replacing anything already loaded.
   */
  load: () => Promise<void>
  /**
   * Loads the page after the last one loaded, appending to the events.
   */
  loadMore: () => Promise<void>
  /**
   * Applies a filter and reloads from the first page.
   */
  setFilter: (filter: ListActivityRequest_Filter) => Promise<void>
  /**
   * Reloads every page of the feed that is currently displayed.
   *
   * Does nothing when the feed is not on screen, as it will be loaded fresh
   * when it is next navigated to. Anything that can invalidate the feed should
   * call this rather than trying to update the events it holds.
   */
  refresh: () => Promise<void>
  /**
   * Marks every comment on the application or policy as read.
   */
  markCommentsRead: (request: MarkAllCommentsReadRequest) => Promise<void>
  /**
   * Marks a single comment as read.
   */
  markCommentRead: (id: number) => Promise<void>
  /**
   * Deletes a comment, removing it from the feed.
   */
  deleteComment: (id: string) => Promise<void>
  /**
   * Changes whether a comment is visible to all users or internal roles only.
   */
  updateCommentVisibility: (id: string, visibility: FeedItemVisibility) => Promise<void>
}

/**
 * Store for the activity feed of the application or policy being displayed.
 *
 * The events are only ever loaded while the feed is on screen. Anything that
 * invalidates them should call {@link FeedStore.refresh}, which is a no-op
 * everywhere else.
 */
export const useFeedStore = defineStore('feed', (): FeedStore => {
  const allEvents = ref<FeedEventLog[]>([])
  const unreadCommentCount = ref(0)
  const hasMoreEvents = ref(false)
  const filter = ref(ListActivityRequest_Filter.FILTER_UNSPECIFIED)
  const isLoading = ref(false)
  const isLoadingMore = ref(false)
  /**
   * The last page loaded into the feed.
   */
  const page = ref(0)

  const events: FeedStore['events'] = computed(() => allEvents.value.filter((e) => filterEvent(e, filter.value)))
  const isLoadingInitial: FeedStore['isLoadingInitial'] = computed(() => isLoading.value && !allEvents.value.length)

  function loadEvents (newEvents: FeedEventLog[], append: boolean = false): void {
    const filteredEvents = normalizeEvents(parseEvents(newEvents))
    allEvents.value = append
      ? uniqBy([...allEvents.value, ...filteredEvents], 'id')
      : filteredEvents
  }

  function setEvent (event: FeedEventLog): void {
    const index = allEvents.value.findIndex((e) => e.id === event.id)
    if (index !== -1) {
      allEvents.value.splice(index, 1, event)
    }
  }

  const apiStore = useApiStore()
  /**
   * Lists `pageCount` pages of events, starting from `requestedPage`.
   */
  async function list (
    requestedPage: number,
    requestedFilter: ListActivityRequest_Filter,
    pageCount: number = 1,
  ): Promise<void> {
    const appContext = useAppContextStore()
    const settingsStore = useSettingsStore()
    const notificationStore = useNotificationStore()
    const policy = isPolicyView()
    isLoading.value = true

    try {
      const id = policy ? appContext.policyId : appContext.applicationId
      if (!id) return

      // always list policy events unless on legacy
      const type = (!settingsStore.protobuf && !policy)
        ? ListActivityRequest_Type.TYPE_APPLICATION
        : ListActivityRequest_Type.TYPE_POLICY

      const { json: items, count, unreadCommentCount: unreadCount } = await apiStore.activity.list({
        id: String(type === ListActivityRequest_Type.TYPE_APPLICATION ? appContext.applicationId : appContext.policyId),
        type,
        // only paginated when using pfactivity, otherwise limit will be ignored
        paginationOptions: {
          page: requestedPage,
          limit: ACTIVITY_PAGE_SIZE * pageCount,
        },
        filter: requestedFilter,
      })

      loadEvents(items, !!requestedPage && requestedFilter === filter.value)
      hasMoreEvents.value = requestedPage * ACTIVITY_PAGE_SIZE + items.length < count
      filter.value = requestedFilter
      // a multi page list starts from `requestedPage`, so the last page loaded is the final one it covers
      page.value = requestedPage + pageCount - 1
      if (unreadCount !== undefined) unreadCommentCount.value = unreadCount
    } catch (error) {
      console.error('Error loading events:', error)
      notificationStore.snackbar.error('Error loading events')
    } finally {
      isLoading.value = false
    }
  }

  const load: FeedStore['load'] = () => list(0, filter.value)

  const loadMore: FeedStore['loadMore'] = async () => {
    isLoadingMore.value = true
    try {
      await list(page.value + 1, filter.value)
    } finally {
      isLoadingMore.value = false
    }
  }

  const setFilter: FeedStore['setFilter'] = (newFilter) => list(0, newFilter)

  const refresh: FeedStore['refresh'] = async () => {
    if (!isFeedView()) return
    await list(0, filter.value, page.value + 1)
  }

  const markCommentsRead: FeedStore['markCommentsRead'] = async (request) => {
    const { json } = await apiStore.activity.markAllCommentsRead(request)

    unreadCommentCount.value = 0
    loadEvents(uniqBy([...json, ...allEvents.value], 'id'))
  }

  const markCommentRead: FeedStore['markCommentRead'] = async (id) => {
    const { json } = await apiStore.comment.markRead(`${id}`)

    setEvent(json)
    unreadCommentCount.value--
  }

  const deleteComment: FeedStore['deleteComment'] = async (id) => {
    await apiStore.comment.delete(id)

    allEvents.value = allEvents.value.filter((e) => `${e.id}` !== id)
  }

  const updateCommentVisibility: FeedStore['updateCommentVisibility'] = async (id, visibility) => {
    const { json } = await apiStore.comment.updateVisibility(id, visibility)

    setEvent(json)
  }

  return {
    events,
    filter: computed(() => filter.value),
    hasMoreEvents: computed(() => hasMoreEvents.value),
    isLoading: computed(() => isLoading.value),
    isLoadingMore: computed(() => isLoadingMore.value),
    isLoadingInitial,
    unreadCommentCount: computed(() => unreadCommentCount.value),

    load,
    loadMore,
    setFilter,
    refresh,
    markCommentsRead,
    markCommentRead,
    deleteComment,
    updateCommentVisibility,
  }
})

hotReloadStore(useFeedStore)
